import { Box, Button, Tooltip } from "@mui/material";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useState } from "react";
import useLocalStorageState from "use-local-storage-state";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { teal } from "../../../../theme/colors";
import { nsKey } from "../../../../utils/storage/partnerStorage";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { dayKey } from "./docValidityModel";

import {
  LensToggle,
  ReferenceColumns,
  ValidityNotice,
} from "./DocValidityPanel";

const formatShortDay = (ms) => formatDate(dayKey(ms), "", "MMM d");

// =====================================================================
// DocumentInsightCards
// =====================================================================
// The summary cards shown above the documents table: "Review progress"
// (the stage track), "Shown vs hidden", Document validity (the reference-day
// columns from DocValidityPanel), and "Linked to".
// Replaces the old inline DocumentReviewStatusBar + DocumentValidityBar
// strip. Colors mirror the status/validity palettes used by the table's
// status pills and the validity widget so the whole tab reads as one system.
//
// Card titles name what the card MEASURES, and deliberately avoid the two
// words that are already spoken for elsewhere on this tab: "status" is the
// per-doc column in the table, and "visibility" is the borrower /
// third-party share flags (documentShareFlags.js) — not the isHidden flag
// this card counts.

// Happy-path review stages, left→right (exception statuses live in their
// own chips). Keys match the canonical documentStatus values. `tint` / `text`
// are the selected-state pair — they mirror the status pill palette used by
// the table so a selected stage reads as the same status everywhere.
const PIPELINE_STAGES = [
  {
    key: "New Upload",
    label: "New Upload",
    color: "#CBD5E1",
    tint: "#F1F5F9",
    text: "#475569",
  },
  {
    key: "Pending Review",
    label: "Pending Review",
    color: "#EF9F27",
    tint: "#FAEEDA",
    text: "#633806",
  },
  {
    key: "Processor Reviewed",
    label: "Processor Reviewed",
    color: "#7C6BD6",
    tint: "#EEEBFA",
    text: "#372A78",
  },
  {
    key: "Accepted",
    label: "Underwriter Accepted",
    color: "#1D9E75",
    tint: "#E1F5EE",
    text: "#085041",
  },
];

const EXCEPTION_STATUSES = [
  // Orange-shifted red, matching the status pill — Incomplete is a problem
  // status like Rejected, just a distinguishable one.
  {
    key: "Incomplete",
    label: "Incomplete",
    color: "#E2603E",
    tint: "#FCEFE9",
    text: "#7A3416",
  },
  {
    key: "Rejected",
    label: "Rejected",
    color: "#E24B4A",
    tint: "#FCEBEB",
    text: "#791F1F",
  },
];

// Each card is now a SECTION of one connected panel (see DocumentInsightCards
// below), not a free-standing card — the panel owns the border / radius /
// shadow, and the sections are butted together with a 1px vertical seam drawn
// by the panel. So no border / radius / shadow here, just the padding + column.
// Shared section styling for ALL four stat sections — this object is the ONLY
// place their padding and background are set, so changing either here moves
// every section at once and they can never drift apart. Per-section sx adds
// nothing but the flex weight (and the donut sections' max-width cap).
const CARD_SX = {
  background: "#ffffff",
  p: "24px",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const cardLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.01em",
  color: "#334155",
};

const cardMetaSx = { fontSize: 11, color: "#64748b", whiteSpace: "nowrap" };

// Gap between a section title row and its content. Shared like CARD_SX so the
// four sections can never drift out of vertical rhythm with each other.
const cardTitleRowSx = { mb: 1.5 };

// ── Inline-SVG donut (no chart lib — same approach as the validity ring) ──
function Donut({
  size = 92,
  thickness = 13,
  segments,
  centerValue,
  centerLabel,
  track = "#eef2f6",
}) {
  const r = (size - thickness) / 2;
  const C = 2 * Math.PI * r;
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  let offset = 0;
  // Scale the center number to the donut, and drop the sub-label on small ones.
  const valueFont = Math.max(13, Math.min(20, Math.round(size * 0.26)));
  const showLabel = centerLabel && size >= 72;
  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(-90deg)" }}
        aria-hidden
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={track}
          strokeWidth={thickness}
        />
        {total > 0 &&
          segments.map((seg, i) => {
            if (!seg.value) return null;
            const len = (seg.value / total) * C;
            const el = (
              <circle
                key={i}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={seg.color}
                strokeWidth={thickness}
                strokeDasharray={`${len} ${C - len}`}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return el;
          })}
      </svg>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 1,
        }}
      >
        <Box
          component="span"
          sx={{ fontSize: valueFont, fontWeight: 700, color: "#0f172a" }}
        >
          {centerValue}
        </Box>
        {showLabel && (
          <Box
            component="span"
            sx={{ fontSize: 9.5, color: "#94a3b8", mt: "2px" }}
          >
            {centerLabel}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Fixed size of the donut sections' ring.
const CONTENT_H = 64;

// The donut sections are fixed-content — a ring plus three or four legend rows
// — so past this width they just grow whitespace. Capping them is what lets
// "Review status progress", the one section whose tiles actually use the room,
// absorb all the slack instead.
const MAX_DONUT_CARD_W = 300;

// The donut sections' legend: one borderless row per item, stacked. Chips
// with borders read as buttons and put four boxes next to a round chart for no
// gain — a plain "● Label ......... N" row is quieter and the counts line up in
// a single column the eye can scan.
function StackedLegend({ rows, activeKey, onToggle }) {
  const items = rows.flat();
  return (
    <Flx column sx={{ flex: 1, minWidth: 0, gap: "3px" }}>
      {items.map((item) => {
        const clickable = !!onToggle;
        const active = activeKey === item.key;
        return (
          <Flx
            key={item.key}
            ac
            jb
            g={1}
            onClick={clickable ? () => onToggle(item.key) : undefined}
            sx={{
              minWidth: 0,
              borderRadius: "4px",
              px: 0.5,
              mx: -0.5,
              cursor: clickable ? "pointer" : "default",
              background: active ? "#eef2f6" : "transparent",
              "&:hover": clickable
                ? { background: active ? "#e6ebf1" : "#f1f5f9" }
                : undefined,
            }}
          >
            <Flx ac g={0.75} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: item.color,
                  flexShrink: 0,
                }}
              />
              <Txt
                sx={{
                  fontSize: 11.5,
                  fontWeight: active ? 600 : 400,
                  color: active ? "#0f172a" : "#475569",
                  lineHeight: "18px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.label}
              </Txt>
            </Flx>
            <Txt
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: "18px",
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0,
              }}
            >
              {item.value}
            </Txt>
          </Flx>
        );
      })}
    </Flx>
  );
}

// Hairline used for the track's outer border and the seams between stages.
const STAGE_LINE = "#e6e9ed";

// The arrow seam between two stages. A 10px square rotated 45° and pinned
// half-outside the stage's right edge: its top + right borders draw a
// right-pointing chevron, and its `background: inherit` masks the straight
// divider line where the arrowhead crosses it. That's what turns four butted
// cells into one track that reads as a progression — a plain `>` glyph between
// separate boxes doesn't, because the divider still cuts straight through.
// `background: inherit` also means the notch follows the stage's own fill, so
// a selected (tinted) stage points its tint into the next segment for free.
const STAGE_ARROW_SX = {
  content: '""',
  position: "absolute",
  right: "-6px",
  top: "50%",
  width: "10px",
  height: "10px",
  transform: "translateY(-50%) rotate(45deg)",
  background: "inherit",
  border: `1px solid ${STAGE_LINE}`,
  borderLeft: "none",
  borderBottom: "none",
  // Above the next stage, which is later in the DOM and would otherwise
  // paint its own background over the arrowhead.
  zIndex: 2,
};

// One stage of the review pipeline: dot + label, the count, and a share bar.
// Used twice over — inside the happy-path track (butted, arrow-seamed) and as
// a `standalone` tile for the exception statuses, which sit outside the track
// because they aren't a step anyone progresses THROUGH.
function StageCell({
  stage,
  count,
  total,
  active,
  dim,
  onToggle,
  isLast,
  standalone,
  sx,
}) {
  const frac = total ? Math.min(count / total, 1) : 0;
  return (
    <Flx
      column
      g={0.4}
      onClick={() => onToggle?.(stage.key)}
      sx={{
        position: "relative",
        minWidth: 0,
        cursor: "pointer",
        opacity: dim ? 0.45 : 1,
        transition: "opacity .12s, background .12s",
        pl: 1.25,
        // Extra right padding on the arrowed stages so the arrowhead never
        // sits on top of the count.
        pr: isLast || standalone ? 1.25 : 2,
        py: 0.85,
        // Explicit white (not transparent) — the arrow's `background: inherit`
        // needs something opaque to mask the seam line with.
        background: active ? stage.tint : "#fff",
        ...(standalone
          ? {
              flexShrink: 0,
              border: `1px solid ${STAGE_LINE}`,
              borderRadius: "8px",
              overflow: "hidden",
            }
          : {
              flex: 1,
              borderRight: isLast ? "none" : `1px solid ${STAGE_LINE}`,
              ...(!isLast && { "&::after": STAGE_ARROW_SX }),
            }),
        "&:hover": { background: active ? stage.tint : "#f7f9fa" },
        ...sx,
      }}
    >
      <Flx ac g={0.6} sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: stage.color,
            flexShrink: 0,
          }}
        />
        <Txt
          sx={{
            fontSize: 10.5,
            fontWeight: active ? 700 : 500,
            color: active ? stage.text || stage.color : "#475569",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {stage.label}
        </Txt>
      </Flx>
      <Flx ac g={0.5}>
        <Txt sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
          {count}
        </Txt>
        <Txt sx={{ fontSize: 10.5, color: "#94a3b8" }}>
          {count === 1 ? "doc" : "docs"}
        </Txt>
      </Flx>
      <Box
        sx={{
          height: 4,
          borderRadius: "999px",
          background: "#eef2f6",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${Math.max(frac * 100, count > 0 ? 6 : 0)}%`,
            background: stage.color,
            borderRadius: "999px",
            transition: "width .2s",
          }}
        />
      </Box>
    </Flx>
  );
}

// ── Review pipeline card ──
// The only section that grows: the donut sections cap at MAX_DONUT_CARD_W and
// this one takes whatever's left, because it's the one whose content actually
// benefits from the room (six tiles side by side vs. a fixed donut + legend).
function ReviewPipelineCard({
  statusCounts,
  activeCount,
  activeStatus,
  onToggleStatus,
}) {
  const total = activeCount || 0;
  const accepted = statusCounts?.["Accepted"] ?? 0;
  const pct = total ? Math.round((accepted / total) * 100) : 0;
  return (
    <Box sx={{ ...CARD_SX, flex: "3 1 480px" }}>
      <Flx ac jb g={1} sx={cardTitleRowSx}>
        <Txt sx={cardLabelSx}>Review status progress</Txt>
        <Txt sx={cardMetaSx}>
          <Box component="span" sx={{ color: "#1D9E75", fontWeight: 700 }}>
            {accepted}
          </Box>{" "}
          of {total} underwriter accepted
          <Box component="span" sx={{ color: "#94a3b8" }}>
            {" "}
            · {pct}%
          </Box>
        </Txt>
      </Flx>
      <Flx ac g={1.5} sx={{ minWidth: 0 }}>
        {/* Happy-path stages — ONE box, not four. The stages are a
            progression (a doc moves New Upload → Pending → Processor →
            Underwriter Accepted), so they read as a single track with a
            chevron between each step rather than four unrelated tiles. Each
            segment is still its own filter toggle. */}
        <Flx
          sx={{
            flex: 1,
            minWidth: 0,
            border: `1px solid ${STAGE_LINE}`,
            borderRadius: "8px",
            background: "#fff",
            overflow: "hidden",
          }}
        >
          {PIPELINE_STAGES.map((st, i) => (
            <StageCell
              key={st.key}
              stage={st}
              count={statusCounts?.[st.key] ?? 0}
              total={total}
              active={activeStatus === st.key}
              dim={activeStatus && activeStatus !== st.key}
              onToggle={onToggleStatus}
              isLast={i === PIPELINE_STAGES.length - 1}
            />
          ))}
        </Flx>

        {/* Exceptions sit past a divider as their OWN tiles: same anatomy as a
            stage, but no arrow seam either side — Incomplete and Rejected are
            terminal states, not steps that lead anywhere, and an arrow between
            them would claim a progression that doesn't exist. */}
        <Box
          sx={{
            width: "1px",
            alignSelf: "stretch",
            flexShrink: 0,
            background: STAGE_LINE,
          }}
        />
        <Flx g={1} sx={{ flexShrink: 0 }}>
          {EXCEPTION_STATUSES.map((st) => (
            <StageCell
              key={st.key}
              standalone
              stage={st}
              count={statusCounts?.[st.key] ?? 0}
              total={total}
              active={activeStatus === st.key}
              dim={activeStatus && activeStatus !== st.key}
              onToggle={onToggleStatus}
              sx={{ width: 116 }}
            />
          ))}
        </Flx>
      </Flx>
    </Box>
  );
}

// ── Shown vs hidden card (donut) ──
// Doubles as the table's hidden-file scope selector: "Total uploaded" shows
// every doc, "Visible" is the default (hidden excluded), "Hidden" shows only
// hidden ones — without leaving the Uploaded tab. `scope` / `onSelectScope`
// are optional; without them the card is a read-only summary (which is what
// the Hidden and Borrower-uploads tabs get, since the scope would contradict
// the tab they're already on).
function VisibilityCard({ visibleCount, hiddenCount, scope, onSelectScope }) {
  const total = (visibleCount || 0) + (hiddenCount || 0);
  const pct = total ? Math.round((visibleCount / total) * 100) : 0;
  const interactive = typeof onSelectScope === "function";
  return (
    <Box sx={{ ...CARD_SX, flex: "1 1 240px", maxWidth: MAX_DONUT_CARD_W }}>
      <Flx ac jb g={1} sx={cardTitleRowSx}>
        <Txt sx={cardLabelSx}>Shown vs hidden</Txt>
        <Txt sx={cardMetaSx}>
          <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
            {pct}%
          </Box>{" "}
          visible
        </Txt>
      </Flx>
      <Flx ac g={2} sx={{ minWidth: 0 }}>
        {/* The ring is the "everything" target — clicking it is the same as
            picking the Total uploaded chip, which is where the active state
            shows. */}
        <Box
          onClick={interactive ? () => onSelectScope("total") : undefined}
          sx={{
            display: "flex",
            flexShrink: 0,
            cursor: interactive ? "pointer" : "default",
            transition: "opacity .12s",
            ...(interactive && { "&:hover": { opacity: 0.82 } }),
          }}
        >
          <Donut
            size={CONTENT_H}
            thickness={11}
            segments={[
              { value: visibleCount, color: teal[500] },
              { value: hiddenCount, color: "#cbd5e1" },
            ]}
            centerValue={total}
            centerLabel="total"
          />
        </Box>
        {/* Row 1: Total uploaded (full width). Row 2: Visible + Hidden. */}
        <StackedLegend
          activeKey={interactive ? scope : undefined}
          onToggle={interactive ? onSelectScope : undefined}
          rows={[
            [
              {
                key: "total",
                label: "Total uploaded",
                color: "#475569",
                value: total,
              },
            ],
            [
              {
                key: "visible",
                label: "Visible",
                color: teal[500],
                value: visibleCount,
              },
              {
                key: "hidden",
                label: "Hidden",
                color: "#cbd5e1",
                value: hiddenCount,
              },
            ],
          ]}
        />
      </Flx>
    </Box>
  );
}

// ── Associations card (donut) ──
// Each doc is classified into ONE bucket by priority (borrower › property ›
// entity › unassigned) so the donut + legend sum to the doc total.
const ASSOC_COLORS = {
  borrower: "#3B82F6",
  property: teal[500],
  entity: "#8B5CF6",
  unassociated: "#cbd5e1",
};

function AssociationsCard({ counts }) {
  const c = counts || {};
  const borrower = c.borrower || 0;
  const property = c.property || 0;
  const entity = c.entity || 0;
  const unassociated = c.unassociated || 0;
  const total = borrower + property + entity + unassociated;
  const linked = total - unassociated;
  return (
    <Box sx={{ ...CARD_SX, flex: "1 1 240px", maxWidth: MAX_DONUT_CARD_W }}>
      <Flx ac jb g={1} sx={cardTitleRowSx}>
        <Txt sx={cardLabelSx}>Linked to</Txt>
        <Txt sx={cardMetaSx}>
          <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
            {linked}
          </Box>{" "}
          of {total} linked
        </Txt>
      </Flx>
      <Flx ac g={2} sx={{ minWidth: 0 }}>
        <Donut
          size={CONTENT_H}
          thickness={11}
          segments={[
            { value: borrower, color: ASSOC_COLORS.borrower },
            { value: property, color: ASSOC_COLORS.property },
            { value: entity, color: ASSOC_COLORS.entity },
            { value: unassociated, color: ASSOC_COLORS.unassociated },
          ]}
          centerValue={total}
          centerLabel="docs"
        />
        {/* 2×2 grid: Borrower · Property / Entity · Unassigned */}
        <StackedLegend
          rows={[
            [
              {
                key: "borrower",
                label: "Borrower",
                color: ASSOC_COLORS.borrower,
                value: borrower,
              },
              {
                key: "property",
                label: "Property",
                color: ASSOC_COLORS.property,
                value: property,
              },
            ],
            [
              {
                key: "entity",
                label: "Entity",
                color: ASSOC_COLORS.entity,
                value: entity,
              },
            ],
          ]}
        />
      </Flx>
    </Box>
  );
}

// ── Collapsible summary header ──

// Segment order for the header's stacked bar: the four happy-path stages in
// pipeline order, then the two exception statuses. So the bar reads
// left-to-right as "how far has this loan's paperwork travelled", with the
// problems parked at the tail rather than mixed into the run.
const HEADER_BAR_SEGMENTS = [
  ...PIPELINE_STAGES.map((st) => ({
    key: st.key,
    label: st.label,
    color: st.color,
  })),
  ...EXCEPTION_STATUSES.map((st) => ({
    key: st.key,
    label: st.label,
    color: st.color,
  })),
];

// One stacked bar standing in for the whole review pipeline. Segments are sized
// by share of the on-screen documents; anything with a status outside the
// taxonomy falls through to a neutral tail segment so the bar's fill always
// equals the count in the headline (an empty gap would read as "missing docs").
function HeaderProgressBar({ statusCounts, total }) {
  const stages = HEADER_BAR_SEGMENTS.map((seg) => ({
    ...seg,
    count: statusCounts?.[seg.key] ?? 0,
  })).filter((seg) => seg.count > 0);
  const counted = stages.reduce((n, seg) => n + seg.count, 0);
  const other = Math.max(0, (total || 0) - counted);

  // The header decides whether a pipeline is worth drawing at all (see
  // showPipeline) — this only bails on an empty set.
  if (!stages.length) return null;
  // The tail is just another segment — folding it in here (rather than
  // rendering it separately) is what lets the seam logic know which segment is
  // genuinely last, so the bar never ends on a stray white edge.
  const segments =
    other > 0
      ? [
          ...stages,
          {
            key: "__other",
            label: "Uncategorized",
            // A step darker than the New Upload segment so the two greys at
            // the ends of the bar do not merge into one.
            color: "#94a3b8",
            count: other,
          },
        ]
      : stages;

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        // Never squeezed to a sliver by the chips to its right, and never so
        // wide that a 3-doc loan draws a banner.
        minWidth: 140,
        maxWidth: 420,
        height: 10,
        borderRadius: "999px",
        overflow: "hidden",
        background: "#eef2f6",
      }}
    >
      {segments.map((seg, i) => (
        <Tooltip
          key={seg.key}
          arrow
          placement="top"
          title={`${seg.label}: ${seg.count}`}
        >
          <Box
            sx={{
              flexGrow: seg.count,
              flexBasis: 0,
              background: seg.color,
              // White seam between stages so two adjacent fills read as two
              // counts, not one blended run. It's a border rather than a gap
              // so the track underneath never shows through as grey.
              ...(i < segments.length - 1 && {
                borderRight: "2px solid #ffffff",
              }),
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
}

// Hairline between the header's clusters (headline · pipeline · attention ·
// scope). The clusters read as four separate answers, and a gap alone doesn't
// carry that on a row this dense — the rule does.
function HeaderDivider({ sx }) {
  return (
    <Box
      sx={{
        width: "1px",
        alignSelf: "stretch",
        my: 0.25,
        flexShrink: 0,
        background: "#e5e7eb",
        ...sx,
      }}
    />
  );
}

// Tones for the header's attention chips. `tint` is the selected fill — a chip
// wired to a filter uses it to show that the table below is scoped to it.
const HEADER_CHIP_TONES = {
  danger: { dot: "#E24B4A", text: "#B42318", line: "#F3C7C5", tint: "#FCEBEB" },
  warning: {
    dot: "#EF9F27",
    text: "#8A5406",
    line: "#F0D6A8",
    tint: "#FAEEDA",
  },
  caution: {
    dot: "#E2603E",
    text: "#8A3D19",
    line: "#F3CBB7",
    tint: "#FCEFE9",
  },
  neutral: {
    dot: "#94a3b8",
    text: "#64748b",
    line: "#e2e8f0",
    tint: "#f1f5f9",
  },
};

// "• 2 expired" — a count of something that needs attention. When `onClick` is
// given the chip is also the filter toggle for that slice, so it stops the
// click from reaching the header row (which would collapse the panel instead).
function HeaderAlertChip({ tone = "neutral", value, label, active, onClick }) {
  const t = HEADER_CHIP_TONES[tone] || HEADER_CHIP_TONES.neutral;
  return (
    <Flx
      ac
      g={0.5}
      onClick={
        onClick
          ? (e) => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      sx={{
        flexShrink: 0,
        border: "1px solid",
        borderColor: active ? t.dot : t.line,
        background: active ? t.tint : "#fff",
        color: t.text,
        borderRadius: "999px",
        px: 1,
        py: "3px",
        fontSize: 11,
        lineHeight: 1,
        whiteSpace: "nowrap",
        cursor: onClick ? "pointer" : "default",
        transition: "background .12s, border-color .12s",
        ...(onClick && { "&:hover": { background: t.tint } }),
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: t.dot,
          flexShrink: 0,
        }}
      />
      <Box component="span" sx={{ fontWeight: 700 }}>
        {value}
      </Box>
      {label}
    </Flx>
  );
}

export const INSIGHTS_EXPANDED_KEY = nsKey("loan-documents-insights-expanded");

/**
 * The full insight panel shown above the documents table — a single connected
 * panel with a collapsible summary header on top and, when expanded, a row of
 * butted sections (Shown vs hidden · Review progress · Expirations · Linked to)
 * separated by 1px seams.
 *
 * `associationCounts` (when provided) adds the Linked-to section.
 */
export default function DocumentInsightCards({
  statusCounts,
  // Docs currently on screen — the denominator for Review progress.
  activeCount,
  // The loan's true non-hidden count. Defaults to activeCount for callers
  // that don't scope the view.
  visibleCount,
  hiddenCount,
  // docValidityModel.buildValidityModel(...) for the docs on screen — the
  // reference days (today / closing, or the frozen close) and every dated
  // doc's status on each.
  validityModel,
  // Jumps to the Document Validity sub-tab (the full timeline).
  onOpenValidityTab,
  reviewFilter,
  onToggleStatus,
  validityFilter,
  onToggleValidity,
  associationCounts,
  // Hidden-file scope: "total" | "visible" | "hidden". Only passed on the
  // Uploaded tab — see VisibilityCard.
  visibilityScope,
  onSelectVisibilityScope,
  // Header title naming the set on screen (the Hidden tab passes
  // "Hidden Documents").
  title = "Uploaded Documents",
  sx,
}) {
  // Collapsed until the user opens it. The header line already carries the
  // summary; the detail sections are a deliberate "show me more", so a first
  // visit shouldn't spend a third of the screen on them before the table.
  // Anyone who has toggled it keeps their stored preference — this only
  // changes the value used when nothing is stored yet.
  const [expanded, setExpanded] = useLocalStorageState(INSIGHTS_EXPANDED_KEY, {
    defaultValue: false,
  });

  // Header summary figures.
  const vis = visibleCount ?? activeCount ?? 0;
  const hid = hiddenCount ?? 0;
  const totalAll = vis + hid;
  const onScreen = activeCount ?? 0;
  const accepted = statusCounts?.["Accepted"] ?? 0;
  const newCount = statusCounts?.["New Upload"] ?? 0;
  const pending = statusCounts?.["Pending Review"] ?? 0;
  const processorReviewed = statusCounts?.["Processor Reviewed"] ?? 0;
  const incomplete = statusCounts?.["Incomplete"] ?? 0;
  const rejected = statusCounts?.["Rejected"] ?? 0;
  // Expired on the day that sets status (today, or the frozen close).
  const statusKey = validityModel?.statusKey || "today";
  const validityRows = validityModel?.rows || [];
  const expired = validityRows.filter(
    (r) => r.at[statusKey] === "expired",
  ).length;
  // Good today, expired by the upcoming closing — needs re-ordering.
  const lapsesBeforeClosing = validityRows.filter((r) => r.lapse).length;
  // Which reference day the validity section shows (null = the status day).
  const [validityDay, setValidityDay] = useState(null);
  const validityRefs = validityModel?.refs || [];
  const shownDayKey = validityRefs.some((r) => r.key === validityDay)
    ? validityDay
    : statusKey;
  // Both days give every document the same answer → no toggle, just say so.
  const sameAtBothDays =
    validityRefs.length === 2 &&
    validityRows.every((r) => r.at.today === r.at.anchor);
  const anchorRef = validityRefs.find((r) => r.key === "anchor");
  const sameLabel = anchorRef
    ? `today and ${anchorRef.role === "frozen" ? "close" : "closing"} (${formatShortDay(anchorRef.dayMs)})`
    : "";
  // Everything still moving through the happy path — the header rolls the
  // three pre-acceptance stages into one number and leaves the per-stage
  // split to the Review progress section below.
  const stillInReview = newCount + pending + processorReviewed;
  // Nothing has been reviewed yet — every doc on screen is still a New Upload.
  // There's no progression to draw, so the bar, the "still in review" count
  // (which would just restate the denominator) and the divider that introduces
  // them all drop out together. The headline and the chips still carry it.
  const showPipeline = onScreen > 0 && newCount !== onScreen;
  // Whether the attention cluster renders at all — drives its leading divider.
  const hasAlerts =
    expired > 0 || lapsesBeforeClosing > 0 || rejected > 0 || incomplete > 0;

  return (
    <Box
      sx={{
        width: "100%",
        background: "#ffffff",
        // Top hairline only — no side borders, so the panel runs full-bleed
        // into the table container below it (which is framed the same way).
        // The top radii went with them: a corner radius with no side border
        // to meet just curls the top rule at each end.
        borderTop: "1px solid #e5e7eb",
        overflow: "hidden",
        // boxShadow:
        //   "rgba(0,0,0,0.06) 0px 6px 10px -3px, rgba(0,0,0,0.05) 0px 3px 5px -4px",
        ...sx,
      }}
    >
      {/* ── Header row (toggles the detail sections) ──
          One line, read left to right: how many are accepted → the stacked
          pipeline → what's still moving → what needs attention → how much of
          the loan is even on screen. It stays identical expanded or collapsed
          so toggling the details doesn't reflow the summary. */}
      <Flx
        ac
        g={2}
        onClick={() => setExpanded((v) => !v)}
        sx={{
          px: 2,
          // Collapsed, the header sits right on the table toolbar: 16 above,
          // 8 below (the toolbar adds its own 8 on top).
          ...(expanded ? { pt: 2, pb: 1.5 } : { pt: 2, pb: 1 }),
          cursor: "pointer",
          userSelect: "none",
          borderBottom: expanded ? "1px solid #e5e7eb" : "none",
          "&:hover": { background: "#fafbfc" },
        }}
      >
        {/* Which set this is ("Uploaded Documents" / "Hidden Documents"), then
            the acceptance count — the one number that answers "are we done?" */}
        <Txt
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            lineHeight: 1,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {title}
        </Txt>
        <HeaderDivider />
        <Flx ac g={0.75} sx={{ flexShrink: 0 }}>
          <Txt
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: "#334155",
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            {accepted}/{onScreen}
          </Txt>
          <Txt sx={{ fontSize: 11.5, color: "#64748b", whiteSpace: "nowrap" }}>
            underwriter accepted
          </Txt>
        </Flx>

        {showPipeline && <HeaderDivider />}

        {showPipeline && (
          <HeaderProgressBar statusCounts={statusCounts} total={onScreen} />
        )}

        {showPipeline && stillInReview > 0 && (
          <Txt
            sx={{
              fontSize: 11.5,
              color: "#94a3b8",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {stillInReview} still in review
          </Txt>
        )}

        {hasAlerts && <HeaderDivider />}

        {/* Attention chips — only the non-zero ones, so a clean loan shows a
            clean header. Each is also the filter toggle for its slice. */}
        <Flx ac g={1} sx={{ flexShrink: 0 }}>
          {expired > 0 && (
            <HeaderAlertChip
              tone="danger"
              value={expired}
              label="expired"
              active={validityFilter === `${statusKey}:expired`}
              onClick={
                onToggleValidity
                  ? () => onToggleValidity(`${statusKey}:expired`)
                  : undefined
              }
            />
          )}
          {lapsesBeforeClosing > 0 && (
            <HeaderAlertChip
              tone="warning"
              value={lapsesBeforeClosing}
              label="expire before closing"
              active={validityFilter === "anchor:lapse"}
              onClick={
                onToggleValidity
                  ? () => onToggleValidity("anchor:lapse")
                  : undefined
              }
            />
          )}
          {rejected > 0 && (
            <HeaderAlertChip
              tone="danger"
              value={rejected}
              label="rejected"
              active={reviewFilter === "Rejected"}
              onClick={
                onToggleStatus ? () => onToggleStatus("Rejected") : undefined
              }
            />
          )}
          {incomplete > 0 && (
            <HeaderAlertChip
              tone="caution"
              value={incomplete}
              label="incomplete"
              active={reviewFilter === "Incomplete"}
              onClick={
                onToggleStatus ? () => onToggleStatus("Incomplete") : undefined
              }
            />
          )}
        </Flx>

        {/* Right rail: the scope of everything to the left, then the toggle.
            `ml: auto` on the divider (not the rail) so the slack opens BEFORE
            the rule — otherwise it would sit tight against the chips with the
            gap on the wrong side of it. */}
        <HeaderDivider sx={{ ml: "auto" }} />
        <Flx ac g={1.25} sx={{ flexShrink: 0 }}>
          <Txt sx={{ fontSize: 11.5, color: "#94a3b8", whiteSpace: "nowrap" }}>
            {onScreen === totalAll
              ? `${totalAll} document${totalAll === 1 ? "" : "s"}`
              : `${onScreen} of ${totalAll} shown`}
          </Txt>
          <Tooltip
            arrow
            placement="top"
            title={expanded ? "Hide details" : "Show details"}
          >
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: 20,
                color: "#94a3b8",
                transition: "transform .15s",
                transform: expanded ? "rotate(180deg)" : "none",
              }}
            />
          </Tooltip>
        </Flx>
      </Flx>

      {/* ── Detail sections — one connected row, 1px seams between them ── */}
      {expanded && (
        <Flx
          sx={{
            alignItems: "stretch",
            // Closes the expanded details off from the table toolbar below.
            borderBottom: "1px solid #e5e7eb",
            "& > div:not(:last-of-type)": {
              borderRight: "1px solid #e5e7eb",
            },
          }}
        >
          {/* Shown vs hidden — temporarily hidden (the Hidden sub-tab covers
              this scope for now). Restore by un-commenting. */}
          {/* <VisibilityCard
            visibleCount={visibleCount ?? activeCount}
            hiddenCount={hiddenCount}
            scope={visibilityScope}
            onSelectScope={onSelectVisibilityScope}
          /> */}
          {/* Document validity — first in the row. ONE reference day at a
              time: the day the status is set on (today, or the close on a
              closed loan) by default, with a Today / At closing toggle in the
              corner. When both days give every document the same answer the
              toggle goes and the line reads "as of today and closing (date)". With only one day (no
              upcoming closing, or a closed loan with no anchor date) the
              notice takes the second slot. "Details" opens the Document
              Validity sub-tab. */}
          {validityModel && validityRows.length > 0 && (
            <Box
              sx={{ flex: "1.4 1 440px", minWidth: 0, position: "relative" }}
            >
              <Flx
                ac
                g={1}
                sx={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}
              >
                {validityRefs.length === 2 && !sameAtBothDays && (
                  <LensToggle
                    small
                    withCompare={false}
                    refs={validityRefs}
                    value={shownDayKey}
                    onChange={setValidityDay}
                  />
                )}
                {onOpenValidityTab && (
                  <Button
                    size="small"
                    variant="text"
                    endIcon={
                      <ArrowForwardRounded
                        sx={{ fontSize: "13px !important" }}
                      />
                    }
                    onClick={onOpenValidityTab}
                    sx={{
                      minWidth: 0,
                      px: 0.75,
                      py: 0.25,
                      fontSize: 11.5,
                      lineHeight: 1.4,
                      textTransform: "none",
                      "& .MuiButton-endIcon": { ml: 0.25 },
                    }}
                  >
                    Details
                  </Button>
                )}
              </Flx>
              <ReferenceColumns
                compact
                refs={validityRefs.filter((r) => r.key === shownDayKey)}
                // Both days agree → one column, and its line says so.
                sourceText={
                  sameAtBothDays
                    ? `Document validity as of ${sameLabel}`
                    : undefined
                }
                placeholder={
                  validityRefs.length === 1 ? (
                    <ValidityNotice
                      model={validityModel}
                      sx={{ width: "100%" }}
                    />
                  ) : null
                }
                model={validityModel}
                filter={validityFilter}
                onFilter={onToggleValidity}
              />
            </Box>
          )}
          <ReviewPipelineCard
            statusCounts={statusCounts}
            activeCount={activeCount}
            activeStatus={reviewFilter}
            onToggleStatus={onToggleStatus}
          />
          {associationCounts && <AssociationsCard counts={associationCounts} />}
        </Flx>
      )}
    </Box>
  );
}

// Every review status with its palette, in pipeline order then the exception
// statuses — the bulk bar's status menu reads the same colors as this panel.
const DOC_STATUS_TONES = [...PIPELINE_STAGES, ...EXCEPTION_STATUSES];

export { DocumentInsightCards, DOC_STATUS_TONES };
